/**
 * New test as a page (v3, phase 4), at /user-testing/new: the same create
 * page as New site (three ways as tiles, the chosen one opens below, "what
 * happens next" on the right), with the test actions. Tests also get "A
 * published site" when the account has a live HTML site, as in the modal.
 *
 *   /user-testing/new               the ways in
 *   /user-testing/new?draft=<id>    resumes an unfinished test draft
 *
 * Only the container changed: same CreateFlow, same buildTestCreateActions,
 * same drafts and the same landing (the new test) after finishing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import firebase from '../../firebase';
import CreateFlow from '../../components/CreateFlow/CreateFlow';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import GlassButton from '../../components/Aero/GlassButton';
import AeroDotLoader from '../../components/Aero/AeroDotLoader';
import { buildTestCreateActions } from './createFlowActions';
import '../../styles/userTesting/index.css';
import '../../styles/aero-ut-theme.css';
import '../AeroSites/AeroSites.css';

export default function AeroTestNew() {
  const { user, shell = {} } = useOutletContext();
  const navigate = useNavigate();
  const { search } = useLocation();
  const draftId = useMemo(() => new URLSearchParams(search).get('draft'), [search]);

  const actions = useMemo(() => (user?.uid ? buildTestCreateActions(user.uid) : null), [user]);

  useEffect(() => { document.title = 'New test | Aero'; }, []);

  // Html sites with a published version enable "One of your sites", the same
  // rule the Tests list used for the modal (read from the shell's site list).
  const publishedSites = useMemo(
    () => (Array.isArray(shell.sites) ? shell.sites.filter((x) => x.type === 'html' && x.activeVersion) : null),
    [shell.sites]
  );

  const [draft, setDraft] = useState(draftId ? undefined : null);
  useEffect(() => {
    if (!draftId || !user?.uid) return undefined;
    let cancelled = false;
    firebase.firestore().collection('user').doc(user.uid).collection('url').doc(draftId).get()
      .then((doc) => {
        if (cancelled) return;
        const d = doc.exists ? doc.data() : null;
        setDraft(d ? { id: draftId, name: d.title || '', draftMeta: d.draftMeta || null } : null);
      })
      .catch(() => { if (!cancelled) setDraft(null); });
    return () => { cancelled = true; };
  }, [draftId, user]);

  const leave = () => {
    if (shell.refresh) shell.refresh();
    navigate('/user-testing');
  };

  return (
    <div className="aero-main aero-sites-page aero-create-page">
      <AeroPageHeader
        crumbs={[{ label: 'Tests', onClick: () => navigate('/user-testing') }, { label: 'New test' }]}
        actions={<GlassButton fill="glass" size="sm" onClick={leave}>Cancel</GlassButton>}
      />
      <div className="aero-sd-body">
        {!actions || draft === undefined ? (
          <div className="aero-create-wait"><AeroDotLoader size="md" label="Loading" /></div>
        ) : (
          <CreateFlow
            key={draftId || 'new'}
            layout="page"
            user={user}
            kind="test"
            heading="New test"
            publishedSites={publishedSites}
            existingDraft={draft}
            actions={actions}
            onClose={leave}
            onFinished={(landing) => {
              if (shell.refresh) shell.refresh();
              navigate(landing || '/user-testing');
            }}
          />
        )}
      </div>
    </div>
  );
}
